import {
  ValidateNested,
  IsDefined,
  IsString,
  IsArray,
  IsEnum,
  IsOptional,
  IsIn,
  IsBoolean,
  ValidateIf,
  Length,
  IsEmail,
  ArrayMinSize,
} from 'class-validator';
import { Type } from 'class-transformer';
import { IsCuid } from 'lib/validation/IsCuid';
import { GetEndpoint, PostEndpoint, PutEndpoint } from 'types/Endpoint';

export enum CommunicationResponseStatus {
  pending = 'pending',
  responded = 'responded',
  expired = 'expired',
}

export type CommunicationStatusFlags = {
  sent: boolean;
  sentDate: ISODateString | null;
  bounced: boolean;
  bouncedDate: ISODateString | null;
  rejected: boolean;
  rejectedDate: ISODateString | null;
  delayed: boolean;
  delayedDate: ISODateString | null;
  opened: boolean;
  openedDate: ISODateString | null;
  responded: boolean;
  respondedDate: ISODateString | null;
  due: boolean;
  dueDate: ISODateString | null;
  expired: boolean;
  expiredDate: ISODateString | null;
  overDue: boolean;
  overDueDate: ISODateString | null;
};

export type EngagePropertiesSortDirection = 'asc' | 'desc';
export type EngagePropertiesSortField = 'contacts' | 'flats';

enum FlatOccupationStatus {
  OCCUPIED = 'occupied',
  UNOCCUPIED = 'unoccupied',
  UNKNOWN = 'unknown',
}

export enum FlatIssueType {
  RESIDENT_ISSUES = 'resident-issues',
  OWNER_ISSUES = 'owner-issues',
  EMAIL_ISSUES = 'email-issues',
}

export enum FlatRequiresResidentSupport {
  REQUIRED = 'required',
  NOT_REQUIRED = 'not-required',
}

export enum FlatEmailDeliveryStatus {
  SENT = 'sent',
  REJECTED = 'rejected',
  BOUNCED = 'bounced',
  DELIVERED = 'delivered',
  DELAYED = 'delayed',
}

export enum EngageFlatIsHMO {
  UNKNOWN = 'UNKNOWN',
  NO = 'NO',
  YES = 'YES',
}

export enum EngageFlatIsCommerciallyOwned {
  UNKNOWN = 'UNKNOWN',
  NO = 'NO',
  YES = 'YES',
}

enum FlatContactType {
  resident = 'resident',
  residentOwner = 'resident-owner',
  owner = 'owner',
  movedSold = 'moved-sold',
  noAssociation = 'no-association',
  other = 'other',
  lettingAgent = 'letting-agent',
  onBehalfOf = 'on-behalf-of',
}

enum EngageContactArchivedReason {
  notMe = 'not-me',
  movedOut = 'moved-out',
  stoppedOwning = 'stopped-owning',
  movedOutStoppedOwning = 'moved-out-stopped-owning',
  mistake = 'mistake',
  other = 'other',
}

enum DataSource {
  'engage' = 'engage',
  'app' = 'app',
}

type ISODateString = string & { __brand: 'ISODate' };

enum CommunicationStatus {
  requested = 'requested',
  sent = 'sent',
  failed = 'failed',
}

enum CommunicationType {
  email = 'email',
  letter = 'letter',
}

enum CommunicationAccessedFrom {
  'letterQr' = 'letter-qr',
  'emailLink' = 'email-link',
  'app' = 'app',
  'shortCode' = 'short-code',
  'unknown' = 'unknown',
}

export type EngageCommunication = {
  id: string;
  createdDate: ISODateString;
  name: string;
  type: CommunicationType;
  contactId: string | null;
  address: string;
  status: CommunicationStatus;
  statusDate: ISODateString;

  link: string;
  linkAccessed: {
    from: CommunicationAccessedFrom;
    date: ISODateString;
  } | null;
  linkStatus: CommunicationResponseStatus;
  statusFlags: CommunicationStatusFlags;
  previewContentURL: string | null;
};

export type EngageContact = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  isOwner: boolean;
  isResident: boolean;
  isUnder16: boolean;
  hasSupportNeeds: boolean;
  hasCognitiveImpairment: boolean;
  hasHearingImpairment: boolean;
  hasMobilityLimitation: boolean;
  hasSocialAnxiety: boolean;
  hasEmergencyEvacuationStatement: boolean;
  hasGivenSupportNeedsConsent: boolean;
  comments: string;
  internalNotes: string;
  contactType: FlatContactType;
  addedTimestamp: ISODateString;
  addedVia: DataSource;
  archivedTimestamp: ISODateString | null;
  archivedVia: DataSource | null;
  archivedReason: EngageContactArchivedReason | null;
  confirmedDate: ISODateString | null;
  confirmationExpiryDate: ISODateString | null;
};

/**
 * Get Properties
 */

export type BlockManagerGetPropertiesRequest = GetEndpoint<{
  url: '/blockmanagement/properties';
  query: {
    page: number;
    search?: string;
    sortBy?: EngagePropertiesSortField;
    sortDirection?: EngagePropertiesSortDirection;
  };
  response: {
    totalCount: number;
    properties: {
      id: string;
      reference: string;
      customReference: string;
      name: string;
      parentName: string;
      addressLines: string[];
      postcode: string;
      organisationId: string;
      engageUrl: string;
      noOfFlats: number;
      noOfContacts: number;
    }[];
    expandedData: {
      organisations: Record<string, { name: string }>;
    };
  };
}>;

/**
 * Onboard Flats
 */

export class BlockManagerOnboardFlatsParams {
  @IsDefined()
  @IsCuid()
  propertyUid!: string;
}

export type BlockManagerOnboardFlatsRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/onboard';
  params: typeof BlockManagerOnboardFlatsParams;
  query: {};
  body: {};
  response: {
    onboarded: number;
    updated: number;
  };
}>;

/**
 * Get Flats
 */

export type BlockManagerGetFlatsRequestFlat = {
  id: string;
  name: string;
  reference: string;
  customReference: string;
  occupationStatus: FlatOccupationStatus;
  isHMO: EngageFlatIsHMO;
  isCommerciallyOwned: EngageFlatIsCommerciallyOwned;
  isMissingResidents: boolean | null;
  issues: {
    hasNoResidents: boolean | null;
    hasNoOwners: boolean | null;
    isMissingResidents: boolean | null;
    hasExpiredResidentConfirmations: boolean | null;
    hasExpiredOwnerConfirmations: boolean | null;
    hasOwnerIssues: boolean;
    hasResidentIssues: boolean;
    hasEmailIssues?: boolean;
  };
  confirmations: {
    oldestResidentConfirmationDate: ISODateString | null;
    oldestOwnerConfirmationDate: ISODateString | null;
    residentConfirmationExpiryDate: ISODateString | null;
    ownerConfirmationExpiryDate: ISODateString | null;
  };
  matchingContactIds: string[];
  contacts: {
    id: string;
    name: string;
    email: string;
    isOwner: boolean;
    isResident: boolean;
    isPrimary: boolean;
    isUnder16: boolean;
    hasSupportNeeds: boolean;
    contactType: FlatContactType;
    confirmedDate: ISODateString | null;
    confirmationExpiryDate: ISODateString | null;
  }[];
};

export type BlockManagerGetFlatsRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats';
  query: {
    page: number;
    search?: string;
    occupationStatuses?: FlatOccupationStatus[];
    issueContactTypes?: FlatIssueType[];
    requiresResidentSupport?: FlatRequiresResidentSupport[];
  };
  response: {
    totalCount: number;
    flats: BlockManagerGetFlatsRequestFlat[];
  };
}>;

/**
 * Get Flat
 */

export type BlockManagerGetFlatRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid';
  query: {};
  response: {
    contacts: EngageContact[];
    communications: EngageCommunication[];
  };
}>;

/**
 * Get Flat Communications
 */
export type BlockManagerGetFlatCommunicationsRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/communications';
  query: {};
  response: {
    communications: EngageCommunication[];
  };
}>;

/**
 * Send Communication
 */
export class BlockManagerSendCommsParams {
  @IsDefined()
  @IsCuid()
  propertyUid!: string;

  @IsDefined()
  @IsCuid()
  flatUid!: string;
}

export class BlockManagerSendCommsBody {
  @IsDefined()
  @IsString()
  communicationTemplateId!: string;

  // @MinLength(1, { each: true }) // this can be an empty array for letters
  @IsDefined()
  @IsArray()
  @IsCuid({ each: true }) // if present, then must be an array of cuids
  contactIds!: string[];
}

export type BlockManagerSendCommunicationRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/sendcommunication';
  body: typeof BlockManagerSendCommsBody;
  params: typeof BlockManagerSendCommsParams;
  query: {};
  response: {
    communications: EngageCommunication[];
  };
}>;

/**
 * Bulk Send Communication
 */
export class BlockManagerSendCommunicationBulkParams {
  @IsDefined()
  @IsCuid()
  propertyUid!: string;
}

export class BlockManagerSendCommunicationBulkBody {
  @IsDefined()
  @IsString()
  communicationTemplateId!: string;

  @IsDefined()
  @IsArray()
  @ArrayMinSize(1)
  @IsCuid({ each: true })
  flatIds!: string[];

  @IsDefined()
  @IsBoolean()
  residents!: boolean;

  @IsDefined()
  @IsBoolean()
  owners!: boolean;

  @IsOptional()
  @IsBoolean()
  primaryContactsOnly?: boolean;
}

export type BlockManagerSendCommunicationBulkRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/sendcommunicationbulk';
  params: typeof BlockManagerSendCommunicationBulkParams;
  body: typeof BlockManagerSendCommunicationBulkBody;
  query: {};
  response: {};
}>;

/**
 * Shared params
 */

export class BlockManagerFlatParams {
  @IsDefined()
  @IsCuid()
  propertyUid!: string;

  @IsDefined()
  @IsCuid()
  flatUid!: string;
}

/**
 * Add Contact
 */

export class BlockManagerAddContactParams extends BlockManagerFlatParams {}

// Return true if contact is 16 or over
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const flatContactIs16OrOver = (data: any): boolean =>
  !data.isUnder16 || !!data.isOwner || !data.isResident;

export class NewFlatContactDTO {
  constructor(data: NewFlatContactDTO) {
    Object.assign(this, data);
  }

  @IsDefined({ message: 'id is required.' })
  @IsString()
  id!: string;

  @ValidateIf((o) => flatContactIs16OrOver(o))
  @IsDefined({ message: 'firstName is required.' })
  @IsString()
  @Length(1, 50)
  firstName!: string;

  @ValidateIf((o) => flatContactIs16OrOver(o))
  @IsDefined({ message: 'lastName is required.' })
  @IsString()
  @Length(1, 50)
  lastName!: string;

  @ValidateIf(
    (o) =>
      typeof o.email !== 'string' ||
      (flatContactIs16OrOver(o) && o.email !== ''),
  )
  @IsString()
  @IsEmail()
  email!: string;

  @ValidateIf(
    (o) =>
      typeof o.phone !== 'string' ||
      (flatContactIs16OrOver(o) && o.phone !== ''),
  )
  @IsString()
  @Length(3, 20)
  phone!: string;

  @IsDefined({ message: 'isResident is required.' })
  @IsBoolean()
  isResident!: boolean;

  @IsDefined({ message: 'isOwner is required.' })
  @IsBoolean()
  isOwner!: boolean;

  // Validate contactType if contact is not an owner or resident
  @ValidateIf((o) => !o.isOwner && !o.isResident)
  @IsEnum(FlatContactType)
  contactType!: FlatContactType;

  @IsString()
  @Length(0, 1000)
  internalNotes!: string;

  @IsString()
  @Length(0, 1000)
  comments!: string;

  @IsDefined({ message: 'isUnder16 is required.' })
  @IsBoolean()
  isUnder16!: boolean;

  @IsOptional()
  @IsBoolean()
  isFormFiller?: boolean;

  @IsDefined({ message: 'hasSupportNeeds is required.' })
  @IsBoolean()
  hasSupportNeeds!: boolean;

  @IsBoolean()
  hasMobilityLimitation!: boolean;

  @IsBoolean()
  hasCognitiveImpairment!: boolean;

  @IsBoolean()
  hasHearingImpairment!: boolean;

  @IsBoolean()
  hasSocialAnxiety!: boolean;

  @IsBoolean()
  hasEmergencyEvacuationStatement!: boolean;

  @IsBoolean()
  hasGivenSupportNeedsConsent!: boolean;

  @IsOptional()
  @IsBoolean()
  isPrimary?: boolean;

  @IsOptional()
  @IsBoolean()
  isContactable?: boolean;
}

export class BlockManagerAddContactBody {
  constructor(data: BlockManagerAddContactBody) {
    Object.assign(this, data);
  }

  @ValidateNested()
  @Type(() => NewFlatContactDTO)
  @IsDefined()
  contact!: NewFlatContactDTO;
}

export type BlockManagerAddContactRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contact';
  params: typeof BlockManagerAddContactParams;
  query: {};
  body: typeof BlockManagerAddContactBody;
  response: {
    message: string;
    flatOccupationStatus: FlatOccupationStatus;
    contact: EngageContact;
  };
}>;

/**
 * Update Contact
 */

export class BlockManagerContactParams extends BlockManagerFlatParams {
  @IsDefined({ message: 'contactUid is required.' })
  @IsCuid()
  contactUid!: string;
}

export class BlockManagerUpdateContactParams extends BlockManagerContactParams {}

export class BlockManagerUpdateContactBody {
  constructor(data: BlockManagerUpdateContactBody) {
    Object.assign(this, data);
  }

  @ValidateNested()
  @Type(() => NewFlatContactDTO)
  @IsDefined({ message: 'contact is required.' })
  contact!: NewFlatContactDTO;
}

export type BlockManagerUpdateContactRequest = PutEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid';
  params: typeof BlockManagerUpdateContactParams;
  query: {};
  body: typeof BlockManagerUpdateContactBody;
  response: {
    flatOccupationStatus: FlatOccupationStatus;
    contact: EngageContact;
  };
}>;

/**
 * Archive / Unarchive Contact
 */

export class BlockManagerArchiveContactParams extends BlockManagerContactParams {
  @IsDefined()
  @IsIn(['archive', 'unarchive'])
  action!: 'archive' | 'unarchive';
}

export class BlockManagerArchiveContactBody {
  constructor(data: BlockManagerArchiveContactBody) {
    Object.assign(this, data);
  }

  @IsOptional()
  @IsEnum(EngageContactArchivedReason)
  archivedReason?: EngageContactArchivedReason;
}

export type BlockManagerArchiveContactRequest = PostEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid/:action';
  params: typeof BlockManagerArchiveContactParams;
  query: {};
  body: typeof BlockManagerArchiveContactBody;
  response: {
    contact: EngageContact | null;
    flatOccupationStatus: FlatOccupationStatus;
  };
}>;

/**
 * Get Contact
 */

export class BlockManagerGetContactParams extends BlockManagerContactParams {}

export type BlockManagerGetContactRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid';
  params: typeof BlockManagerGetContactParams;
  query: {};
  response: {
    contact: EngageContact;
  };
}>;

export class BlockManagerListContactsQuery {
  @IsOptional()
  @IsIn(['active', 'archived', 'both'])
  state?: 'active' | 'archived' | 'both';
}

export type BlockManagerListContactsRequest = GetEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts';
  params: typeof BlockManagerFlatParams;
  query: BlockManagerListContactsQuery;
  response: {
    contacts: EngageContact[];
  };
}>;

/**
 * Create Flat
 */

export class BlockManagerLinkFlatElementParams {
  @IsCuid()
  @IsDefined()
  element_uid!: string;
}

type BlockManagerLinkFlatElementResponse = {
  message: string;
  flatElement: {
    propertyId: number; // ID of property in RiskBase
    assetId: number; // ID of flat in RiskBase
    groupId: number; // ID of field group that assetId is in
    organisationId: number;
    flatId: number | null;
    flatUid: string | null;
  };
  linkId?: number;
  created: boolean;
};

export type BlockManagerLinkFlatElementRequest = PutEndpoint<{
  url: '/blockmanagement/create-flat-element/:element_uid';
  params: typeof BlockManagerLinkFlatElementParams;
  body: {};
  query: {};
  response: BlockManagerLinkFlatElementResponse;
}>;

/**
 * Update Flat
 */

export class BlockManagerUpdateFlatParams {
  @IsCuid()
  @IsDefined({ message: 'propertyUid is required.' })
  propertyUid!: string;

  @IsCuid()
  @IsDefined({ message: 'flatUid is required.' })
  flatUid!: string;
}

export class BlockManagerUpdateFlatBody {
  constructor(data: BlockManagerUpdateFlatBody) {
    Object.assign(this, data);
  }

  @IsOptional()
  @IsEnum(FlatOccupationStatus)
  occupationStatus?: FlatOccupationStatus;

  @IsOptional()
  @IsEnum(EngageFlatIsHMO)
  isHMO?: EngageFlatIsHMO;

  @IsOptional()
  @IsEnum(EngageFlatIsCommerciallyOwned)
  isCommerciallyOwned?: EngageFlatIsCommerciallyOwned;
}

type BlockManagerUpdateFlatResponse = {
  updatedContacts: EngageContact[];
  archivedContactIds: string[];
  isHMO: EngageFlatIsHMO;
  isCommerciallyOwned: EngageFlatIsCommerciallyOwned;
};

export type BlockManagerUpdateFlatRequest = PutEndpoint<{
  url: '/blockmanagement/properties/:propertyUid/flats/:flatUid';
  params: typeof BlockManagerUpdateFlatParams;
  body: typeof BlockManagerUpdateFlatBody;
  query: {};
  response: BlockManagerUpdateFlatResponse;
}>;
